import React from 'react';
import { motion } from 'framer-motion';
import { CloudIcon, BoltIcon, ArrowPathIcon } from '@heroicons/react/24/outline';

const InvSlide11Technical: React.FC = () => {
  const innovations = [
    {
      icon: CubeIcon,
      title: 'Dynamic Module Loading',
      desc: 'Every learning module is dynamically imported but lives off a CDN. No rebuild required to update content.',
      color: '#0ea5e9',
    },
    {
      icon: CloudIcon,
      title: 'Save Eventually Queues',
      desc: 'Everything cached locally. Data synced when connection available. Works on poor internet.',
      color: '#10b981',
    },
    {
      icon: BoltIcon,
      title: 'Minimal Server Load',
      desc: 'Static content is cheap. Logic runs locally. Small payloads. $600/year server handles 1,000 concurrent students.',
      color: '#f59e0b',
    },
    {
      icon: ArrowPathIcon,
      title: 'Hot Updates',
      desc: 'Modules can be modified without redeploying. No app recompilation. Changes go live instantly.',
      color: '#8b5cf6',
    },
  ];

  return (
    <div className="flex flex-col items-center justify-center h-full px-16">
      <motion.h2
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-5xl font-bold text-white mb-3 text-center"
      >
        Technical Innovation
      </motion.h2>
      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2 }}
        className="text-xl text-[#94a3b8] mb-10 text-center max-w-4xl"
      >
        Built for scale from day one — designed to handle millions of students affordably
      </motion.p>

      <div className="grid grid-cols-2 gap-6 max-w-5xl mb-8">
        {innovations.map((item, index) => (
          <motion.div
            key={item.title}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.3 + index * 0.1 }}
            className="flex gap-5 p-5 rounded-xl"
            style={{
              background: `${item.color}10`,
              border: `1px solid ${item.color}30`,
            }}
          >
            <div
              className="w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0"
              style={{
                background: `${item.color}20`,
                border: `2px solid ${item.color}50`,
              }}
            >
              <item.icon className="w-7 h-7" style={{ color: item.color }} />
            </div>
            <div>
              <h3 className="text-xl font-semibold text-white mb-2">{item.title}</h3>
              <p className="text-[#94a3b8]">{item.desc}</p>
            </div>
          </motion.div>
        ))}
      </div>

      {/* The Scale Problem */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.7 }}
        className="w-full max-w-5xl"
      >
        <div
          className="p-6 rounded-xl"
          style={{
            background: 'rgba(30, 41, 59, 0.5)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
          }}
        >
          <h3 className="text-xl font-semibold text-white mb-4">The Scale Challenge (Solved)</h3>

          <div className="flex items-center gap-8">
            <div className="flex-1 text-[#94a3b8]">
              <p className="mb-3">
                <span className="text-white font-medium">Problem:</span> Unlike e-commerce (250K customers accessing once a week),
                we could have 100,000 students logging in simultaneously.
              </p>
              <p>
                <span className="text-white font-medium">Solution:</span> Save-eventually queues, local logic execution,
                vectorized images, code-based lessons (no heavy assets).
              </p>
            </div>

            <div className="flex flex-col gap-3">
              <div className="px-4 py-2 rounded-lg bg-[#10b981]/20 text-center">
                <div className="text-2xl font-bold text-[#10b981]">$600/yr</div>
                <div className="text-xs text-[#94a3b8]">Server cost</div>
              </div>
              <div className="px-4 py-2 rounded-lg bg-[#0ea5e9]/20 text-center">
                <div className="text-2xl font-bold text-[#0ea5e9]">1,000</div>
                <div className="text-xs text-[#94a3b8]">Concurrent students</div>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

// Custom CubeIcon for the first item
function CubeIcon({ className, style }: { className?: string; style?: React.CSSProperties }) {
  return (
    <svg className={className} style={style} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M21 7.5l-9-5.25L3 7.5m18 0l-9 5.25m9-5.25v9l-9 5.25M3 7.5l9 5.25M3 7.5v9l9 5.25m0-9v9" />
    </svg>
  );
}

export default InvSlide11Technical;
